import React from 'react'
import { motion } from 'framer-motion'
import {
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  AdjustmentsHorizontalIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'

interface SpamDetectionData {
  status: 'active' | 'learning' | 'inactive'
  recent_scans: Array<{
    timestamp: string
    content_preview: string
    spam_score: number
    risk_level: 'very_low' | 'low' | 'medium' | 'high' | 'critical'
    action_taken: 'approved' | 'flagged' | 'blocked'
  }>
  rules_active: number
  detection_stats: {
    total_scanned: number
    spam_detected: number
    false_positives: number
    accuracy_rate: number
  }
  risk_distribution: {
    very_low: number
    low: number
    medium: number
    high: number
    critical: number
  }
}

interface SpamDetectionOverviewProps {
  data?: SpamDetectionData
  onConfigureRules?: () => void
}

const SpamDetectionOverview: React.FC<SpamDetectionOverviewProps> = ({ data, onConfigureRules }) => {
  const status = data?.status || 'inactive'
  const recentScans = data?.recent_scans || []
  const rulesActive = data?.rules_active || 0
  const stats = data?.detection_stats
  const riskDistribution = data?.risk_distribution

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-green-600 bg-green-100'
      case 'learning':
        return 'text-blue-600 bg-blue-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'very_low':
        return 'text-green-600 bg-green-100'
      case 'low':
        return 'text-blue-600 bg-blue-100'
      case 'medium':
        return 'text-yellow-600 bg-yellow-100'
      case 'high':
        return 'text-orange-600 bg-orange-100'
      case 'critical':
        return 'text-red-600 bg-red-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'approved':
        return <CheckIcon className="w-4 h-4 text-green-600" />
      case 'flagged':
        return <ExclamationTriangleIcon className="w-4 h-4 text-yellow-600" />
      case 'blocked':
        return <XMarkIcon className="w-4 h-4 text-red-600" />
      default:
        return null
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <ShieldCheckIcon className="w-6 h-6 text-green-600" />
          <h3 className="text-lg font-semibold text-gray-900">Spam Detection</h3>
        </div>
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
          <span className="capitalize">{status}</span>
        </span>
      </div>

      {/* Detection Stats */}
      {stats && (
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <p className="text-2xl font-semibold text-green-600">{(stats.accuracy_rate || 0).toFixed(1)}%</p>
            <p className="text-sm text-green-700">Accuracy Rate</p>
          </div>
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <p className="text-2xl font-semibold text-blue-600">{rulesActive}</p>
            <p className="text-sm text-blue-700">Active Rules</p>
          </div>
        </div>
      )}

      {/* Risk Distribution */}
      {riskDistribution && (
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
            <ChartBarIcon className="w-4 h-4 mr-2" />
            Risk Distribution
          </h4>
          <div className="space-y-2">
            {Object.entries(riskDistribution).map(([risk, count]) => (
              <div key={risk} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getRiskColor(risk)}`}>
                    {(risk || 'low').replace('_', ' ')}
                  </span>
                </div>
                <span className="text-sm font-medium text-gray-900">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Scans */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Recent Scans</h4>
        <div className="space-y-2">
          {recentScans.length > 0 ? (
            recentScans.slice(0, 4).map((scan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900 truncate">
                    {scan.content_preview || 'No preview available'}
                  </p>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${getRiskColor(scan.risk_level || 'low')}`}>
                      {(scan.risk_level || 'low').replace('_', ' ')}
                    </span>
                    <span className="text-xs text-gray-500">
                      Score: {scan.spam_score || 0}/100
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-2 ml-3">
                  {getActionIcon(scan.action_taken || 'approved')}
                  <span className="text-xs text-gray-500 capitalize">
                    {scan.action_taken || 'approved'}
                  </span>
                </div>
              </motion.div>
            ))
          ) : (
            <p className="text-sm text-gray-500 italic">No recent scans</p>
          )}
        </div>
      </div>

      {/* Performance Summary */}
      {stats && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Performance Summary</h4>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-gray-600">Total Scanned</p>
              <p className="font-semibold text-gray-900">{(stats.total_scanned || 0).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-gray-600">Spam Detected</p>
              <p className="font-semibold text-red-600">{(stats.spam_detected || 0).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-gray-600">False Positives</p>
              <p className="font-semibold text-yellow-600">{stats.false_positives || 0}</p>
            </div>
          </div>
        </div>
      )}

      {/* Settings Link */}
      <div className="pt-4 border-t border-gray-200">
        <button
          onClick={onConfigureRules}
          className="w-full flex items-center justify-center space-x-2 px-3 py-2 text-sm font-medium text-green-600 bg-green-50 rounded-md hover:bg-green-100 transition-colors"
        >
          <AdjustmentsHorizontalIcon className="w-4 h-4" />
          <span>Configure Detection Rules</span>
        </button>
      </div>
    </div>
  )
}

export default SpamDetectionOverview